import { format } from 'date-fns';
import { dashboardsMock } from '@/mocks';
import DEFAULT_OPERATIONS from './user.gql';
import { useLazyQuery } from '@apollo/client';
import { CustomerDataProps, DashboardsProps } from '@/types';
import { getOnStorage, setOnStorage, updateOnStorage, removeFromStorage } from '@/shared';
import * as ordersStore from '@/shared/ordersStore';
import { useState, useEffect, useContext, useCallback, createContext, useRef } from 'react';
import { useFetchAxios, useGlobals, useNetworkStatusContext, useTables } from '../index';
import { unregisterServiceWorker } from '@/shared/serviceWorkFunctions';

interface UserContextProps {
  email: string;
  taxVat: string;
  getCustomer: any;
  valorMinimo: number;
  userToken: string;
  isSignedIn: boolean;
  sendingOrders: boolean;
  dashboards: DashboardsProps;
  customer: CustomerDataProps;
  rcaSaldoFlex: number | null;

  signOut: (e?: React.MouseEvent<HTMLAnchorElement>) => void;
  fetchSaldoFlex: () => void;
  handleOfflineOrders: () => void;
  setEmail: (value: string) => void;
  setTaxVat?: (value: string) => void;
  setUserToken?: (value: string) => void;
  setIsSignedIn: (value: boolean) => void;
  getValorMinimo: (loja: number) => void;
  setRcaSaldoFlex?: (value: number) => void;
  setCustomer: (value: CustomerDataProps) => void;
}

const UserContext = createContext<UserContextProps>({} as UserContextProps);

const CHECK_INTERVAL = 5 * 60 * 1000;   // 5 min — how often the sync loop wakes up
const ENABLE_INTERVAL = 10 * 60 * 1000; // 10 min — throttle between actual send batches

const UserProvider: React.FC<any> = ({ children }) => {
  // CONSTANTS
  const { getCustomerQuery } = DEFAULT_OPERATIONS;

  // CONTEXTS
  const { tables } = useTables();
  const { version } = useGlobals();
  const { axiosCtx } = useFetchAxios();
  const { connectivityStatus, checkConnectivity } = useNetworkStatusContext();

  // FUNCTIONS
  const updateLastSendTime = () => {
    setOnStorage('last_order_send_time', Date.now());
  };

  const canSendOrders = () => {
    const lastSendTime = getOnStorage('last_order_send_time');
    const now = Date.now();

    if (!lastSendTime) {
      updateLastSendTime();
      return true;
    }

    // Verifica se passou 2 minutos desde o último envio
    const enable = now - lastSendTime >= ENABLE_INTERVAL;
    console.info(`Verificação janela de tempo para enviar pedidos ${format(now, "HH'h' mm'm' ss's'")} :`, enable);
    return enable;
  };

  // REFERENCES
  const refTimer = useRef<NodeJS.Timeout | null>(null);
  // Legacy `refSendingOrders` removed — ordersStore.acquireForSend's lease
  // is the actual mutex now. Per-order_number Ref was insufficient against
  // multiple concurrent `processOfflineOrders` invocations (the loss path).
  const refMigrationDone = useRef(false);

  // STATES
  const [userToken] = useState('');
  const [email, setEmail] = useState('');
  const [taxVat, setTaxVat] = useState('');
  const [sendingOrders, setSendingOrders] = useState(false);
  const [valorMinimo, setValorMinimo] = useState(0);
  const [dashboards] = useState(dashboardsMock as DashboardsProps);
  const [rcaSaldoFlex, setRcaSaldoFlex] = useState<number | null>(() => {
    const rcaCustomer = getOnStorage('rca_usuario');
    return rcaCustomer?.saldoFlex || null;
  });
  const [customer, setCustomer] = useState<CustomerDataProps>(() => {
    const rcaCustomer = getOnStorage('rca_usuario');
    if (rcaCustomer) return rcaCustomer;
    return {} as CustomerDataProps;
  });
  const [isSignedIn, setIsSignedIn] = useState(() => {
    const token = !!getOnStorage('token_magento');
    const rcaCustomer = !!getOnStorage('rca_usuario');
    return token && rcaCustomer;
  });

  // QUERIES
  const [getCustomer, { error: errorCustomer, loading: loadingCustomer }] = useLazyQuery(getCustomerQuery);

  // CALLBACKS
  const getValorMinimo = useCallback(async (loja: number) => {
    const parametrosAws = tables.current.parametros_aws;

    const vlrMin = parametrosAws?.find((p) => {
      if (p.DESC_PARAMETRO?.toUpperCase() !== 'VLR_MINIMO_PEDIDO') return false;

      return p.COD_LOJA === loja;
    })?.VALOR_PARAMETRO;

    setValorMinimo(vlrMin ? Number(vlrMin) : 0);
  }, [setValorMinimo]);

  /**
   * Push pending orders to /integra-pedido. Delegates queue mutation to
   * ordersStore — that module's lease + queue-lock pair are the actual
   * mutex; this function just supplies the POST sender.
   *
   * Replaces the legacy `processOfflineOrders` which had two loss modes:
   *   1) read-modify-write race on the `offline_orders` JSON array
   *   2) per-order-number `refSendingOrders` flag failed to guard against
   *      multiple concurrent invocations (route nav + interval + connectivity
   *      flip all called it in parallel).
   */
  const drainPendingOrders = useCallback(async () => {
    if (window.location.pathname.includes('login')) return;
    if (connectivityStatus !== 'online') return;

    setSendingOrders(true);
    try {
      await ordersStore.processPendingOrders(
        async (record) => {
          try {
            const res = await axiosCtx.post('/integra-pedido', {
              ...record.payload,
              origin_function: 'offline',
              current_time: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
              version: version || document.getElementById('version-id')?.textContent || '',
            });
            return Boolean((res as any)?.data?.message) ? { ok: true } : { ok: false, error: 'no_data_message' };
          } catch (error: any) {
            const status = error?.response?.status;
            // 4xx (except 408/429) are unlikely to succeed on retry — mark
            // permanent so we don't burn retries on a bad payload.
            const permanent = typeof status === 'number' && status >= 400 && status < 500 && status !== 408 && status !== 429;
            return { ok: false, error: error?.message || String(error), permanent };
          }
        },
        {
          rcaCodPessoa:
            ((getOnStorage('rca_usuario') as any)?.codPessoa != null
              ? String((getOnStorage('rca_usuario') as any).codPessoa)
              : null),
        }
      );
    } finally {
      setSendingOrders(false);
    }
  }, [axiosCtx, connectivityStatus, version]);

  /**
   * Reconcile orders that the API acked but the ERP may not yet have
   * received. Confirmed orders are deleted from the queue; orders still
   * missing from `pedido-notifica-get` after the grace window go back to
   * pending for retry. Replaces the legacy `rescueStuckSynchedOrders`,
   * which only deleted (Suspect #2 in orders-issues.md).
   *
   * NOTE: Phase 2 of the plan — retry is gated behind backend idempotency
   * on /integra-pedido. Until the backend confirms it dedups by
   * `order_number`, ackGraceMs is intentionally large (1 hour) and
   * MAX_ATTEMPTS keeps retries low so any duplication is bounded.
   */
  const reconcileAckedOrders = useCallback(async () => {
    if (connectivityStatus !== 'online' || window.location.pathname.includes('login')) return;
    if (!canSendOrders()) return;
    updateLastSendTime();

    const rca = getOnStorage('rca_usuario') as any;
    const codRca = rca?.codPessoa;
    const base = (window as any)?.agp_config?.REACT_APP_AWS;
    if (!codRca || !base) return;

    await ordersStore.reconcileAckedOrders(
      async () => {
        const pedidos = await axiosCtx
          .get(`${base}/pedido-notifica-get?seqpessoa=${codRca}`)
          .then((res) => res?.data?.pedidos || []);
        return new Set<string>(
          pedidos.map((p: any) =>
            p?.orders?.order_number_origem || p?.orders?.order_number_origin || p?.orders?.order_number
          )
        );
      },
      { ackGraceMs: 60 * 60 * 1000 } // 1h grace until backend idempotency is confirmed
    );
  }, [axiosCtx, connectivityStatus]);

  const signOut = useCallback(async (e?: React.MouseEvent<HTMLAnchorElement>) => {
    e?.preventDefault?.();
    const isOnline = await checkConnectivity();

    const tokenOffline = getOnStorage('token_magento');
    setOnStorage('token_offline', tokenOffline || '')
    removeFromStorage('token_magento');
    removeFromStorage('selected_brand');
    
    if (isOnline) {
      await Promise.all([
        // removeFromStorage('login_date'),
        // removeFromStorage('token_aws'),
        // removeFromStorage('rca'),
        removeFromStorage('selected_client'),
        removeFromStorage('selected_rca'),
        removeFromStorage('new_data_sets'),
        // removeFromStorage('discounts'),
        // removeFromStorage('rca_usuario'),
        // localStorage.removeItem('localTime'),
        localStorage.removeItem('login_bootstrap'),
      ]);

      setEmail('');
      setTaxVat('');
      setRcaSaldoFlex(null);
      setCustomer({} as CustomerDataProps);
    }

    setIsSignedIn(false);
    // if (isOnline) unregisterServiceWorker({ preserveLocalStorage: true });
    window.location.reload();
  }, [checkConnectivity]);

  const handleOfflineOrders = useCallback(() => {
    // Backwards-compatible export used by route changes (routes/index.tsx)
    // and by other talons that previously triggered `processOfflineOrders`
    // directly. Now a thin wrapper around the ordersStore-backed drain.
    void drainPendingOrders();
  }, [drainPendingOrders]);

  // One-shot legacy-store migration, runs after the user is signed in and
  // online so any unsynced orders from the previous app version are not
  // stranded in the old `offline_orders` / `synched_orders` localStorage
  // arrays. Idempotent inside ordersStore via the migration marker.
  useEffect(() => {
    if (refMigrationDone.current) return;
    if (!isSignedIn) return;
    refMigrationDone.current = true;
    ordersStore
      .migrateLegacyOrders({
        onError: (err, ctx) => console.error('[ordersStore migration]', ctx, err),
      })
      .then((result) => {
        if (result.migrated > 0) {
          console.info(`[ordersStore] migrated ${result.migrated} legacy orders.`);
        }
      })
      .catch((err) => console.error('[ordersStore] migration failed', err));
  }, [isSignedIn]);

  // Periodic sync: drain pending orders and reconcile acked orders against
  // the ERP. Lock + lease in ordersStore prevent concurrent passes from
  // racing each other.
  useEffect(() => {
    if (connectivityStatus !== 'online') {
      if (refTimer.current) {
        clearInterval(refTimer.current);
        refTimer.current = null;
      }
      return;
    }

    if (!!refTimer.current) return;

    refTimer.current = setInterval(() => {
      if (!canSendOrders()) return;

      void drainPendingOrders();
      void reconcileAckedOrders();
    }, CHECK_INTERVAL);

    return () => {
      if (refTimer.current) {
        clearInterval(refTimer.current);
        refTimer.current = null;
      }
    };
  }, [connectivityStatus, drainPendingOrders, reconcileAckedOrders]);

  // On connectivity flip / login, drain immediately rather than waiting
  // for the next interval tick. Same lock + lease protect against running
  // alongside the interval.
  useEffect(() => {
    if (connectivityStatus !== 'online' || !isSignedIn) return;
    void drainPendingOrders();
    void reconcileAckedOrders();
  }, [connectivityStatus, isSignedIn, drainPendingOrders, reconcileAckedOrders]);

  const fetchSaldoFlex = async () => {
    const rcaCustomer = getOnStorage('rca_usuario');
    if (!rcaCustomer) return;

    const fetchedSaldoFlex = tables.current.saldo_flex;

    const codPessoa = rcaCustomer?.codPessoa;
    if (!codPessoa) return;

    const parseNumberLoose = (value: any): number | null => {
      if (value === null || value === undefined) return null;
      if (typeof value === 'number') return Number.isFinite(value) ? value : null;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return null;
        const normalized = trimmed.includes(',') ? trimmed.replace(/\./g, '').replace(',', '.') : trimmed;
        const num = Number(normalized);
        return Number.isFinite(num) ? num : null;
      }
      return null;
    };

    // Novo formato: { [COD_PESSOA]: { VLR_ERP } }
    const node = fetchedSaldoFlex?.[String(codPessoa)] ?? null;
    const saldoERPValue = node?.VLR_ERP ?? null;
    const finalValue = parseNumberLoose(saldoERPValue);

    if (finalValue !== null) {
      const updatedCustomer = {
        ...rcaCustomer,
        saldoFlex: finalValue,
        saldoERP: finalValue,
      };
      updateOnStorage('rca_usuario', updatedCustomer);
      setRcaSaldoFlex(finalValue);
      setCustomer(updatedCustomer);
    }
  };

  // Valida se o usuário está logado e valida o saldo flex atualiando o saldo flex do usuário
  useEffect(() => {
    const token = getOnStorage('token_magento');
    const rcaCustomer = getOnStorage('rca_usuario');
    const signInStatus = !!(token && rcaCustomer);

    if (signInStatus !== isSignedIn) setIsSignedIn(signInStatus);
    if (!customer && rcaCustomer && signInStatus) setCustomer(rcaCustomer || ({} as CustomerDataProps));

    if (signInStatus && rcaSaldoFlex === null) {
      fetchSaldoFlex();
    }
  }, [isSignedIn]);

  // Atualiza o saldo flex do usuário
  useEffect(() => {
    if (rcaSaldoFlex !== null) {
      const rcaCustomer = getOnStorage('rca_usuario');
      if (rcaCustomer) {
        updateOnStorage('rca_usuario', {
          ...rcaCustomer,
          saldoFlex: rcaSaldoFlex,
        });
      }
    }
  }, [rcaSaldoFlex]);

  // Valida se o usuário está logado
  useEffect(() => {
    const token = !!getOnStorage('token_magento');
    const rcaCustomer = !!getOnStorage('rca_usuario');
    setIsSignedIn(token && rcaCustomer);
  }, []);

  // Se não tiver customer, desloga o usuário
  useEffect(() => {
    if (!customer) signOut();
  }, [errorCustomer, loadingCustomer, customer, signOut]);

  return (
    <UserContext.Provider
      value={{
        email,
        taxVat,
        customer,
        userToken,
        isSignedIn,
        dashboards,
        valorMinimo,
        rcaSaldoFlex,
        sendingOrders,

        signOut,
        setEmail,
        setTaxVat,
        getValorMinimo,
        handleOfflineOrders,
        setIsSignedIn,
        getCustomer,
        setCustomer,
        setRcaSaldoFlex,
        fetchSaldoFlex,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

const useUserContext = () => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }

  return context;
};

export { UserProvider, useUserContext };
